import { useState, useRef, useEffect } from "react";
import { Send, Edit3, Save, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";

export default function ChatBot() {
  const [messages, setMessages] = useState([
    { sender: "bot", text: "👋 Hi! I'm your chatbot. How can I help you today?" },
  ]);
  const [chatbot, setChatbot] = useState();
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [editResponse, setEditResponse] = useState("");
  const [responses, setResponses] = useState([]);

  const messagesEndRef = useRef(null);

  // Auto-scroll + Fetch chatbot data & saved edits
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

    const token = localStorage.getItem("token");

    const fetch_data = async () => {
      const response = await fetch("https://zaichat.net/api/get_chatbot_info", {
        headers: {
          auth: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log(data.data[0])
      setChatbot(data.data[0]);
    };

    const fetchSaved = async () => {
      const res = await fetch("https://zaichat.net/api/save_edit_response", {
        headers: { "content-type": "application/json", auth: `Bearer ${token}` },
        method: "GET",
      });

      const data = await res.json();
      setResponses(data.data);
    };

    fetch_data();
    fetchSaved();
  }, [messages]);

  // SEND MESSAGE
  const send = async () => {
    if (!message) {
      toast.error("You must type a message");
      return;
    }

    const webhookUrl = "https://n8n.srv1004057.hstgr.cloud/webhook/playground";
    const session_id = Math.random().toString(36).substring(2, 10);

    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text: message,
          id: session_id,
          chatbot: chatbot,
        }),
      });

      const data = await res.json();

      const botReply = {
        sender: "bot",
        text: data.output.general_response || "I didn't understand that 😅",
        data: data.output.data?.length > 0 ? data.output.data : null,
      };

      setMessages((prev) => [...prev, botReply]);
      setResponse(botReply.text);
      toast.success("Bot replied!");

    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "❌ Something went wrong connecting to server." },
      ]);
      toast.error("Server error");
    }
  };

  // ENABLE EDITING
  const edit = () => {
    if (!response) {
      toast.error("You must ask something first");
      return;
    }
    setEditResponse(response);
  };

  // SAVE EDITED RESPONSE
  const saveEditResponse = async () => {
    if (!editResponse) {
      toast.error("You have not edited the response");
      return;
    }

    const token = localStorage.getItem("token");

    try {
      const res = await fetch("https://zaichat.net/api/save_edit_response", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          auth: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: message, response: editResponse }),
      });

      const data = await res.json();

      setResponses((prev) => [
        { message: message, response: editResponse },
        ...prev,
      ]);

      toast.success("Response saved!");
      setEditResponse("");

    } catch (err) {
      toast.error("Error saving response");
    }
  };

  const SaveALL = async () => {
    const token = localStorage.getItem("token");

    try {
      const res = await fetch("https://zaichat.net/api/SaveALL", {
        method: "POST",
        headers: { "Content-Type": "application/json", auth: `Bearer ${token}` },
      });

      toast.success("All responses saved!");

    } catch (err) {
      toast.error("Failed saving all");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-cream/60 py-10 px-6 md:px-10">

      {/* 🔥 PAGE DESCRIPTION */}

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-5xl md:text-6xl font-extrabold text-primary">
          ChatBot Playground
        </h1>
        <p className="text-dark/70 mt-3 text-lg max-w-2xl mx-auto">
          Test your chatbot responses and fine-tune them here.
        </p>
      </motion.div>
            <div className="max-w-4xl mx-auto text-center mb-10">
        <p className="text-dark/70 text-lg">
          This page allows you to test your chatbot, see how it responds,
          and edit any reply to improve accuracy.
        </p>
        <p className="text-dark/60 text-sm mt-2">
          Any edited responses will be saved automatically on the right section.
        </p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* LEFT SIDE */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10"
        >
          <h2 className="text-2xl font-bold text-primary mb-6 flex items-center gap-2">
            <Send className="w-6 h-6 text-accent" />
            Test Your Bot
          </h2>

          {/* INPUT */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-primary mb-2">
              Your Message
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Type your test message..."
                className="flex-1 px-4 py-3 border-2 border-accent/20 rounded-xl focus:outline-none focus:border-accent transition-all"
                onKeyDown={(e) => e.key === "Enter" && send()}
              />
              <button
                onClick={send}
                className="bg-gradient-to-r from-accent to-orange-500 text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                Send
              </button>
            </div>
          </div>

          {/* BOT RESPONSE */}
          {response && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-primary">
                  Bot Response
                </label>
                <button
                  onClick={edit}
                  className="text-accent font-semibold flex items-center gap-1"
                >
                  <Edit3 className="w-4 h-4" />
                  Edit
                </button>
              </div>
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-primary">
                <p dir="rtl">{response}</p>
              </div>
            </motion.div>
          )}

          {/* EDIT SECTION */}
          {editResponse && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
              <label className="block text-sm font-semibold text-primary mb-2">
                Edit Response
              </label>
              <textarea
                dir="rtl"
                value={editResponse}
                onChange={(e) => setEditResponse(e.target.value)}
                className="w-full px-4 py-3 border-2 border-accent/20 rounded-xl focus:border-accent transition-all min-h-[100px]"
              />
              <button
                onClick={saveEditResponse}
                className="mt-3 bg-gradient-to-r from-accent to-orange-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 hover:scale-105"
              >
                <Save className="w-5 h-5" />
                Save Changes
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* RIGHT SIDE */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="bg-white rounded-3xl shadow-xl p-8 border border-accent/10"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-primary flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-accent" />
              Saved Responses
            </h2>

            {responses.length > 0 && (
              <button
                onClick={SaveALL}
                className="bg-gradient-to-r from-primary to-teal-600 text-white px-4 py-2 rounded-xl font-semibold hover:scale-105 text-sm"
              >
                Save All
              </button>
            )}
          </div>

          {/* SAVED ITEMS */}
          {responses && responses.length > 0 ? (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {responses.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-cream/30 border border-accent/10 rounded-xl p-5"
                >
                  <p className="text-xs font-semibold text-accent uppercase mb-1">
                    Message
                  </p>
                  <p className="text-primary font-medium">{item.message}</p>

                  <p className="text-xs font-semibold text-accent uppercase mb-1 mt-3">
                    Response
                  </p>
                  <p className="text-dark/80" dir="rtl">
                    {item.response}
                  </p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-dark/60">No saved responses yet</div>
          )}
        </motion.div>
      </div>

      <div className="mt-20 text-center text-dark/60 text-sm">
        Powered by <span className="text-accent font-semibold">Zuccess AI</span>
      </div>

      <div ref={messagesEndRef}></div>
    </div>
  );
}

